import { Link } from "react-router-dom";
import { Mail, ChevronRight, Loader2 } from "lucide-react";
import {
  Badge,
  BreadcrumbPage,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
} from "@eduviaos/ui-kit";
import { SettingsBreadcrumbs } from "@/components/AppBreadcrumbs";
import { useTemplateList } from "@/lib/emailTemplates/api";

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function EmailTemplatesListPage() {
  const list = useTemplateList();

  return (
    <div className="grid gap-6">
      <PageHeader>
        <PageHeaderTop>
          <SettingsBreadcrumbs>
            <BreadcrumbPage>Email templates</BreadcrumbPage>
          </SettingsBreadcrumbs>
        </PageHeaderTop>
        <PageHeaderHeading>
          <PageHeaderTitle>Email templates</PageHeaderTitle>
          <PageHeaderDescription>
            The transactional emails the platform sends. Edits go live without a redeploy.
            Per-tenant overrides take precedence over the global template; tenants without an
            override use the global one.
          </PageHeaderDescription>
        </PageHeaderHeading>
      </PageHeader>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>All templates</CardTitle>
              <CardDescription>
                {list.data?.items.length ?? 0} key(s) registered in the catalogue
              </CardDescription>
            </div>
            {list.isFetching && (
              <Loader2 className="text-muted-foreground size-4 animate-spin" aria-hidden="true" />
            )}
          </div>
        </CardHeader>
        <CardContent className="grid gap-2">
          {list.isLoading ? (
            <p className="text-muted-foreground py-8 text-center text-sm">Loading…</p>
          ) : list.isError ? (
            <p className="text-destructive py-8 text-center text-sm">
              {(list.error as Error).message ?? "Failed to load templates."}
            </p>
          ) : (
            list.data!.items.map((t) => {
              const disabled = !t.is_implemented;
              const inner = (
                <div
                  className={
                    "bg-card flex items-center justify-between gap-4 rounded-md border px-4 py-3 transition " +
                    (disabled ? "opacity-60" : "hover:border-primary/40 hover:bg-muted/30")
                  }
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="bg-primary/10 text-primary grid size-8 shrink-0 place-items-center rounded-md">
                      <Mail className="size-4" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 font-medium">
                        {t.display_name}
                        {!t.is_implemented && (
                          <Badge variant="warn" className="text-[10px]">
                            Coming soon
                          </Badge>
                        )}
                        {t.is_implemented && t.has_global_override && (
                          <Badge variant="success" className="text-[10px]">
                            Customised
                          </Badge>
                        )}
                        {t.tenant_overrides_count > 0 && (
                          <Badge variant="info" className="text-[10px]">
                            {t.tenant_overrides_count} tenant override
                            {t.tenant_overrides_count === 1 ? "" : "s"}
                          </Badge>
                        )}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">{t.description}</p>
                      <p className="text-muted-foreground mt-0.5 text-[11px]">
                        Last edited {formatDate(t.last_global_update)}
                      </p>
                    </div>
                  </div>
                  {!disabled && (
                    <ChevronRight
                      className="text-muted-foreground size-4 shrink-0"
                      aria-hidden="true"
                    />
                  )}
                </div>
              );
              return disabled ? (
                <div key={t.key}>{inner}</div>
              ) : (
                <Link
                  key={t.key}
                  to={`/settings/email-templates/${t.key}`}
                  className="focus-visible:ring-ring rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                >
                  {inner}
                </Link>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
